import { useEffect, useRef, useState } from "react";

import {
  Button,
  DialogDescription,
  DialogTitle,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
} from "@upstageai/ui";
import { useTranslation } from "react-i18next";

import ErrorDialog from "./ErrorDialog";
import { EXCEED_NON_GROUPING_ADD } from "../../constants/ErrorMessage";
import { Line } from "../../types/extractorResult";
import { parsingTableStore } from "../../utils/mobx/ParsingTableStore";
import { resultInteractionStore } from "../../utils/mobx/ResultInteractionStore";
import { boxChangedFocusMover } from "../../utils/resultScrollHelper";
import { Strip } from "../../utils/stringUtil";
import { cn } from "../../utils/style";
import { Icon, Icons } from "../Icon";

interface NonGroupingAddDialogProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const getFieldCounts = (lines: Line[], key: string) => {
  return lines.filter(line => line.key === key).length;
};

const DEFAULT_SELECTED_KEY_IDX = -1;

export default function NonGroupingAddDialog({
  isOpen,
  setIsOpen,
}: NonGroupingAddDialogProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [selectedKeyIdx, setSelectedKeyIdx] = useState<number>(
    DEFAULT_SELECTED_KEY_IDX,
  );

  const [errorOn, setErrorOn] = useState<boolean>(false);
  const [errorType, setErrorType] = useState<number>(1);
  const [errorReloadFunction, setErrorReloadFunction] = useState<() => void>(
    () => {},
  );

  const { ontologies, addLineByKey, lineGroups } = parsingTableStore;
  const [lineKeys, setLinekeys] = useState<string[]>([]);
  const { setFocusIndex, setSelectedBoxIds } = resultInteractionStore;

  useEffect(() => {
    setSelectedKeyIdx(DEFAULT_SELECTED_KEY_IDX);
  }, [isOpen]);

  useEffect(() => {
    const onthologySet: Set<string> = new Set();
    if (!ontologies) return;
    // nonGrouping영역만.
    ontologies
      .filter(ontology => ontology.group_type === -1)
      .forEach(ontology => {
        onthologySet.add(Strip(Strip(ontology.key, ".header"), ".content"));
      });
    setLinekeys(Array.from(onthologySet));
  }, [ontologies]);

  const handleAddField = () => {
    const fieldCnt = getFieldCounts(lineGroups, ontologies[selectedKeyIdx].key);
    if (fieldCnt < 100) {
      const [idx, boxId] = addLineByKey(lineKeys[selectedKeyIdx] as string);
      // TODO : 중복된 field 선택 시 idx가 한 칸 전으로 세팅되는 오류 해결
      setFocusIndex(idx);
      setSelectedBoxIds([String(boxId)]);
      boxChangedFocusMover([], "center", "instant", idx);
      setIsOpen(false);
    } else {
      setErrorOn(true);
      setErrorType(EXCEED_NON_GROUPING_ADD);
      setErrorReloadFunction(() => () => {
        setErrorOn(false);
      });
    }
  };

  return (
    <>
      <ErrorDialog
        open={errorOn}
        setOpen={setErrorOn}
        errorType={errorType}
        errorReloadFunction={errorReloadFunction}
      />
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent
          ref={ref}
          onClick={e => {
            e.stopPropagation();
          }}
        >
          <DialogHeader>
            <DialogTitle>{t(`actions.add_field`)}</DialogTitle>
            <DialogDescription>{t(`messages.add_field`)}</DialogDescription>
          </DialogHeader>
          <div className={"flex max-h-[400px] items-center rounded-lg border"}>
            <div className="h-full w-full divide-y overflow-y-scroll px-4 py-1">
              {lineKeys.length === 0 ? (
                <div className="flex items-center justify-center p-4">
                  <p className="text-muted-dark">{t(`common.no_field`)}</p>
                </div>
              ) : (
                lineKeys.map((key, idx) => (
                  <div
                    onClick={() => {
                      setSelectedKeyIdx(idx);
                    }}
                    className={
                      idx === selectedKeyIdx
                        ? "text-primary"
                        : "text-muted-dark"
                    }
                    key={idx}
                  >
                    <div key={idx} className="flex h-9 w-full items-center">
                      <label
                        htmlFor={`key-${idx}`}
                        className="w-full min-w-[340px] max-w-[390px] select-none truncate"
                      >
                        {key}
                      </label>
                      <div
                        className={cn("ml-3 flex h-6 flex-1 items-center", {
                          invisible: selectedKeyIdx !== idx,
                        })}
                      >
                        <Icon source={Icons.Check} />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsOpen(false)}>
              {t(`actions.cancel`)}
            </Button>
            <Button
              variant="primary"
              disabled={selectedKeyIdx === -1}
              onClick={() => {
                handleAddField();
              }}
            >
              {t(`actions.add`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
